document.addEventListener('alpine:init', () => {
  const CTR_BAB_STORAGE_KEY = 'ctr-bab:state';
  const CTR_BAB_STORAGE_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

  Alpine.data('ctrBuildABox', (config) => ({
    sectionId: config.sectionId,
    products: [],
    productRefs: [],
    bundleEntries: [],
    selectedVariantMap: {},
    typePurchase: config.subscriptionDefault ? 'subscribe' : 'one-time',
    selectedFrequency: '',
    frequencyOptions: config.frequencyOptions || [],
    tierConfig: config.tierConfig || [],
    skioProductPlatformId: config.skioProductPlatformId || '',
    skioBoxId: config.skioBoxId || '',
    skioPublicKey: config.skioPublicKey || '',
    fallbackSellingPlanId: config.fallbackSellingPlanId || '',
    sellingPlanNameFilter: config.sellingPlanNameFilter || 'DynamicBoxSubscription',
    skioDynamicBox: null,
    skioError: '',
    isLoadingProducts: false,
    productsError: '',
    debugEnabled: true,
    skioDynamicBoxProductDebug: null,
    drawerOpen: false,
    popupProduct: null,
    isAddingToCart: false,
    errorMessage: '',

    init() {
      const dataEl = document.getElementById('ctr-bab-data-' + this.sectionId);
      if (!dataEl) {
        this.productsError = 'Build-a-Box section data was not found.';
        return;
      }

      try {
        const raw = JSON.parse(dataEl.textContent);
        this.frequencyOptions = raw.frequencyOptions || this.frequencyOptions;
        this.tierConfig = (raw.tierConfig || this.tierConfig).sort((a, b) => a.threshold - b.threshold);
        this.skioProductPlatformId = raw.skioProductPlatformId || this.skioProductPlatformId || '';
        this.skioBoxId = raw.skioBoxId || this.skioBoxId || '';
        this.skioPublicKey = raw.skioPublicKey || this.skioPublicKey;
        this.fallbackSellingPlanId = String(raw.fallbackSellingPlanId || this.fallbackSellingPlanId || '');
        this.sellingPlanNameFilter = raw.sellingPlanNameFilter || this.sellingPlanNameFilter;
        this.debugEnabled = raw.debugEnabled !== false;
        this.skioDynamicBoxProductDebug = raw.skioDynamicBoxProductDebug || null;
        this.typePurchase = raw.subscriptionDefault ? 'subscribe' : 'one-time';
        this.selectedFrequency = this.frequencyOptions[0]?.value || this.frequencyOptions[0]?.label || '';
        this.products = (raw.products || []).filter(Boolean);
        this.freeProduct = raw.freeProduct || null;

      } catch (error) {
        console.error('ctr-build-a-box: failed to parse section data', error);
        this.productsError = 'Build-a-Box section data could not be parsed.';
      }

      this.debug('init', {
        sectionId: this.sectionId,
        productsCount: this.products.length,
        productTitles: this.products.map((product) => product.title),
        skioProductPlatformId: this.skioProductPlatformId,
        skioBoxId: this.skioBoxId,
        skioDynamicBoxProductDebug: this.skioDynamicBoxProductDebug,
        fallbackSellingPlanId: this.fallbackSellingPlanId,
        sellingPlanNameFilter: this.sellingPlanNameFilter,
        tierConfig: this.tierConfig,
      });

      this.initializeAdminProducts();
      this.restoreState();

      this.$watch('drawerOpen', (open) => {
        document.body.style.overflow = open && window.innerWidth < 990 ? 'hidden' : '';
      });
      this.$watch('popupProduct', (product) => {
        document.body.style.overflow = product ? 'hidden' : '';
      });

      this.$watch('bundleEntries', () => this.saveState());
      this.$watch('selectedVariantMap', () => this.saveState());
      this.$watch('typePurchase', () => this.saveState());
      this.$watch('selectedFrequency', () => this.saveState());
    },

    saveState() {
      try {
        const payload = {
          savedAt: Date.now(),
          bundleEntries: this.bundleEntries,
          selectedVariantMap: this.selectedVariantMap,
          typePurchase: this.typePurchase,
          selectedFrequency: this.selectedFrequency,
        };
        localStorage.setItem(CTR_BAB_STORAGE_KEY, JSON.stringify(payload));
      } catch (error) {
        // localStorage unavailable (private mode, quota exceeded) — silently skip
      }
    },

    loadState() {
      try {
        const raw = localStorage.getItem(CTR_BAB_STORAGE_KEY);
        if (!raw) return null;
        const data = JSON.parse(raw);
        if (!data || typeof data !== 'object') return null;
        if (!data.savedAt || Date.now() - data.savedAt > CTR_BAB_STORAGE_TTL_MS) {
          localStorage.removeItem(CTR_BAB_STORAGE_KEY);
          return null;
        }
        return data;
      } catch (error) {
        return null;
      }
    },

    clearState() {
      try {
        localStorage.removeItem(CTR_BAB_STORAGE_KEY);
      } catch (error) {
        // ignore
      }
    },

    restoreState() {
      const state = this.loadState();
      if (!state) return;

      if (state.typePurchase === 'subscribe' || state.typePurchase === 'one-time') {
        this.typePurchase = state.typePurchase;
      }

      if (
        state.selectedFrequency &&
        this.frequencyOptions.some((opt) => String(opt.value) === String(state.selectedFrequency))
      ) {
        this.selectedFrequency = state.selectedFrequency;
      }

      if (state.selectedVariantMap && typeof state.selectedVariantMap === 'object') {
        Object.entries(state.selectedVariantMap).forEach(([productId, variantId]) => {
          const product = this.products.find((p) => String(p.id) === String(productId));
          if (!product) return;
          const variant = (product.variants || []).find(
            (v) => String(v.id) === String(variantId)
          );
          if (variant) {
            this.selectedVariantMap[String(productId)] = variant.id;
          }
        });
      }

      if (Array.isArray(state.bundleEntries)) {
        const valid = state.bundleEntries
          .filter((entry) => {
            if (!entry || entry.productId == null || entry.variantId == null) return false;
            const product = this.products.find(
              (p) => String(p.id) === String(entry.productId)
            );
            if (!product) return false;
            const variant = (product.variants || []).find(
              (v) => String(v.id) === String(entry.variantId)
            );
            return Boolean(variant && variant.available);
          })
          .map((entry) => ({
            productId: String(entry.productId),
            variantId: String(entry.variantId),
            quantity: Math.max(1, parseInt(entry.quantity, 10) || 1),
          }));
        this.bundleEntries = valid;
      }

      this.debug('restore-state', {
        restoredEntries: this.bundleEntries.length,
        typePurchase: this.typePurchase,
        selectedFrequency: this.selectedFrequency,
      });
    },

    debug(label, payload) {
      if (!this.debugEnabled) return;
      console.info(`[ctr-build-a-box][${this.sectionId}] ${label}`, payload);
    },

    initializeAdminProducts() {
      this.selectedVariantMap = {};
      this.bundleEntries = [];
      this.productRefs = this.products.map((product) => ({
        handle: product.handle,
        productPlatformId: product.platformId,
        productId: product.id,
        title: product.title,
        image: product.image,
        variants: product.variants || [],
      }));

      this.products.forEach((product) => {
        const initialVariant = this.getInitialVariant(product);
        if (initialVariant) this.selectedVariantMap[String(product.id)] = initialVariant.id;
      });

      if (!this.products.length) {
        this.productsError = 'Select bundle products in the section settings.';
      }

      this.isLoadingProducts = false;
      this.debug('admin-products', {
        count: this.products.length,
        products: this.products.map((product) => ({
          id: product.id,
          handle: product.handle,
          title: product.title,
          variantsCount: product.variants?.length || 0,
          hasProductDescription: Boolean(product.productDescriptionHtml),
          productDescriptionLength: product.productDescriptionHtml?.length || 0,
        })),
      });
    },

    async fetchSkioDynamicBox() {
      if (!this.skioProductPlatformId) {
        this.debug('skio:skip-missing-product-platform-id', {});
        this.productsError = 'Select a Skio Dynamic Box product in the section settings.';
        this.isLoadingProducts = false;
        return;
      }

      try {
        const headers = {
          'Content-Type': 'application/json',
        };

        const body = {
          productPlatformId: this.normalizeSkioProductId(this.skioProductPlatformId),
        };

        this.debug('skio:request', {
          url: 'https://api.skio.com/storefront-http/get-dynamic-box',
          body,
          hasPublicKey: false,
        });

        const response = await this.fetchSkioDynamicBoxResponse(body, headers);
        this.debug('skio:fetch:resolved', {
          ok: response.ok,
          status: response.status,
        });

        const responseText = await response.text();
        this.debug('skio:fetch:text', responseText);
        this.debug('skio:http-response', {
          ok: response.ok,
          status: response.status,
          body: responseText,
        });

        let parsedResponse = null;
        try {
          parsedResponse = responseText ? JSON.parse(responseText) : null;
        } catch (parseError) {
          throw new Error(`Skio response ${response.status}: invalid JSON`);
        }

        if (!response.ok || parsedResponse?.ok === false) {
          const detail = parsedResponse?.extensions?.detail || parsedResponse?.message || response.status;
          throw new Error(`Skio response ${response.status}: ${detail}`);
        }

        this.skioDynamicBox = parsedResponse;
        this.debug('skio:response', this.skioDynamicBox);
        this.applySkioDiscountTiers();
        this.applySkioSellingPlans();
      } catch (error) {
        this.skioError = error?.message || 'Unable to load Dynamic Box data';
        console.error(`[ctr-build-a-box][${this.sectionId}] skio:fetch:error`, error);
        this.debug('skio:error', {
          message: this.skioError,
          error,
        });
        console.warn('ctr-build-a-box: Skio Dynamic Box fetch failed', error);
      } finally {
        this.isLoadingProducts = false;
      }
    },

    async fetchSkioDynamicBoxResponse(body, headers) {
      const url = 'https://api.skio.com/storefront-http/get-dynamic-box';
      const jsonBody = JSON.stringify(body);

      try {
        this.debug('skio:xhr:start-json', body);
        return await this.xhrPost(
          url,
          jsonBody,
          headers,
          5000,
          'Skio XHR JSON request timed out after 5 seconds'
        );
      } catch (error) {
        this.debug('skio:xhr:json-failed', error);
      }

      this.debug('skio:xhr:start-simple', body);
      return this.xhrPost(
        url,
        jsonBody,
        {},
        8000,
        'Skio XHR simple request timed out after 8 seconds'
      );
    },

    xhrPost(url, body, headers = {}, timeoutMs, timeoutMessage) {
      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', url, true);
        xhr.timeout = timeoutMs;
        Object.entries(headers || {}).forEach(([key, value]) => {
          xhr.setRequestHeader(key, value);
        });
        xhr.onload = () => {
          this.debug('skio:xhr:load', {
            status: xhr.status,
            length: xhr.responseText?.length || 0,
          });
          resolve({
            ok: xhr.status >= 200 && xhr.status < 300,
            status: xhr.status,
            text: () => Promise.resolve(xhr.responseText || ''),
          });
        };
        xhr.onerror = () => {
          reject(new Error('Skio XHR network error'));
        };
        xhr.ontimeout = () => {
          this.debug('skio:xhr:timeout', timeoutMessage);
          reject(new Error(timeoutMessage));
        };
        xhr.send(body);
      });
    },

    normalizeSkioProductId(id) {
      const raw = String(id || '').trim();
      if (!raw) return '';
      return this.normalizeNumericId(raw);
    },

    applySkioDiscountTiers() {
      const tiers =
        this.skioDynamicBox?.percentDiscountTier ||
        this.skioDynamicBox?.DynamicBox?.percentDiscountTier ||
        this.skioDynamicBox?.dynamicBox?.percentDiscountTier ||
        this.skioDynamicBox?.box?.percentDiscountTier ||
        null;

      if (!tiers || typeof tiers !== 'object') return;

      this.tierConfig = Object.entries(tiers)
        .map(([threshold, discount]) => ({
          threshold: Number(threshold),
          discount: Number(discount),
        }))
        .filter((tier) => Number.isFinite(tier.threshold) && Number.isFinite(tier.discount))
        .sort((a, b) => a.threshold - b.threshold);
      this.debug('skio:percent-discount-tier-applied', this.tierConfig);
    },

    applySkioSellingPlans() {
      const groups = [
        this.skioDynamicBox?.sellingPlanGroup,
        this.skioDynamicBox?.DynamicBox?.sellingPlanGroup,
        this.skioDynamicBox?.dynamicBox?.sellingPlanGroup,
        this.skioDynamicBox?.box?.sellingPlanGroup,
      ].filter(Boolean);

      const plans = groups.flatMap((group) =>
        Array.isArray(group)
          ? group
          : group.sellingPlans || group.selling_plans || Object.entries(group).map(([id, plan]) => ({ id, ...plan }))
      );

      this.frequencyOptions = plans.map((plan) => ({
        label: plan.title || plan.name || 'Subscription',
        value: String(plan.id || plan.platformId || '').replace('gid://shopify/SellingPlan/', ''),
      }));
      this.selectedFrequency = this.frequencyOptions[0]?.value || '';
      this.debug('skio:selling-plans-applied', this.frequencyOptions);
    },

    normalizeNumericId(id) {
      const raw = String(id || '').trim();
      if (!raw) return '';
      const match = raw.match(/(\d+)$/);
      return match ? match[1] : raw;
    },

    getInitialVariant(product) {
      return (product.variants || []).find((variant) => variant.available) || product.variants?.[0] || null;
    },

    getSelectedVariant(product) {
      const selectedId = this.selectedVariantMap[String(product.id)];
      return (
        (product.variants || []).find((variant) => String(variant.id) === String(selectedId)) ||
        this.getInitialVariant(product)
      );
    },

    selectVariant(product, optionIndex, value) {
      const current = this.getSelectedVariant(product);
      const nextOptions = [...(current?.options || [])];
      nextOptions[optionIndex] = value;

      const nextVariant =
        (product.variants || []).find((variant) =>
          nextOptions.every((optionValue, index) => !optionValue || variant.options?.[index] === optionValue)
        ) || current;

      if (nextVariant) this.selectedVariantMap[String(product.id)] = nextVariant.id;
    },

    getOptionValues(product, optionIndex) {
      return [...new Set((product.variants || []).map((variant) => variant.options?.[optionIndex]).filter(Boolean))];
    },

    findBundleEntry(variantId) {
      const key = String(variantId);
      return this.bundleEntries.find((entry) => String(entry.variantId) === key) || null;
    },

    getVariantQuantity(product) {
      const variant = this.getSelectedVariant(product);
      if (!variant) return 0;
      const entry = this.findBundleEntry(variant.id);
      return entry ? entry.quantity : 0;
    },

    incrementSelectedVariant(product) {
      const variant = this.getSelectedVariant(product);
      if (!variant) return;
      this.incrementVariant(variant.id);
    },

    decrementSelectedVariant(product) {
      const variant = this.getSelectedVariant(product);
      if (!variant) return;
      this.decrementVariant(variant.id);
    },

    findBundleEntryIndex(variantId) {
      const key = String(variantId);
      return this.bundleEntries.findIndex((entry) => String(entry.variantId) === key);
    },

    addProduct(product) {
      const variant = this.getSelectedVariant(product);
      if (!variant) return;
      const existing = this.findBundleEntry(variant.id);
      if (existing) {
        existing.quantity += 1;
      } else {
        this.bundleEntries.push({
          productId: String(product.id),
          variantId: String(variant.id),
          quantity: 1,
        });
      }
    },

    incrementVariant(variantId) {
      const entry = this.findBundleEntry(variantId);
      if (entry) entry.quantity += 1;
    },

    decrementVariant(variantId) {
      const index = this.findBundleEntryIndex(variantId);
      if (index === -1) return;
      const entry = this.bundleEntries[index];
      if (entry.quantity <= 1) {
        this.bundleEntries.splice(index, 1);
      } else {
        entry.quantity -= 1;
      }
    },

    removeVariant(variantId) {
      const index = this.findBundleEntryIndex(variantId);
      if (index !== -1) this.bundleEntries.splice(index, 1);
    },

    getSelectedProducts() {
      return this.bundleEntries
        .map((entry) => {
          const product = this.products.find((item) => String(item.id) === String(entry.productId));
          if (!product) return null;
          const variant = (product.variants || []).find(
            (item) => String(item.id) === String(entry.variantId)
          );
          if (!variant) return null;
          return {
            product,
            variant,
            quantity: entry.quantity,
            variantId: entry.variantId,
          };
        })
        .filter(Boolean);
    },

    getTierCountForProduct(product) {
      return Number(product.tierCount || 1);
    },

    getSelectedItemCount() {
      return this.getSelectedProducts().reduce(
        (sum, entry) => sum + entry.quantity * this.getTierCountForProduct(entry.product),
        0
      );
    },

    getActiveTier() {
      const count = this.getSelectedItemCount();
      return (
        [...this.tierConfig].reverse().find((tier) => count >= tier.threshold) || {
          threshold: 0,
          discount: 0,
        }
      );
    },

    getNextTier() {
      const count = this.getSelectedItemCount();
      return this.tierConfig.find((tier) => count < tier.threshold) || null;
    },

    getSubtotal() {
      // One-time baseline: regular variant price × qty
      return this.getSelectedProducts().reduce(
        (sum, entry) => sum + entry.variant.price * entry.quantity,
        0
      );
    },

    getVariantSubscriptionPrice(variant, preferredPlanId) {
      const allocations = variant?.sellingPlanAllocations || [];
      if (!allocations.length) return Number(variant?.price) || 0;

      // Try plan IDs in order of preference: the one that will be sent to cart,
      // then the user's selected frequency, then any first allocation.
      const candidates = [preferredPlanId, this.selectedFrequency]
        .filter(Boolean)
        .map(String);

      for (const planId of candidates) {
        const match = allocations.find(
          (allocation) => String(allocation.selling_plan_id) === planId
        );
        if (match && match.price != null) return Number(match.price);
      }

      const firstPrice = allocations[0]?.price;
      return firstPrice != null ? Number(firstPrice) : Number(variant?.price) || 0;
    },

    getSubscriptionSubtotal() {
      // Use the same plan ID that cart-add will use, so preview matches the cart.
      const planId = this.getSellingPlanId();
      return this.getSelectedProducts().reduce(
        (sum, entry) => sum + this.getVariantSubscriptionPrice(entry.variant, planId) * entry.quantity,
        0
      );
    },

    applyTierDiscount(amount) {
      if (amount === 0) return 0;
      const tierDiscount = Number(this.getActiveTier().discount) || 0;
      return Math.max(0, Math.round(amount * (1 - tierDiscount / 100)));
    },

    getOneTimePrice() {
      return this.applyTierDiscount(this.getSubtotal());
    },

    getSubscribePrice() {
      return this.applyTierDiscount(this.getSubscriptionSubtotal());
    },

    getFinalTotal() {
      return this.typePurchase === 'subscribe'
        ? this.getSubscribePrice()
        : this.getOneTimePrice();
    },

    getSavings() {
      return Math.max(0, this.getSubtotal() - this.getFinalTotal());
    },

    // -------------------------------------------------------------------------
    // Subscribe & Save badge — dynamic, stacked discount
    // -------------------------------------------------------------------------

    /**
     * Helper: % off between variant.price and its selling plan allocation price.
     * Honors the configured fallbackSellingPlanId when present; otherwise uses
     * the first allocation on the variant.
     */
    _allocationDiscountPercent(variant) {
      if (!variant || !Array.isArray(variant.sellingPlanAllocations) || variant.sellingPlanAllocations.length === 0) {
        return 0;
      }

      const fallbackId = this.fallbackSellingPlanId
        ? String(this.fallbackSellingPlanId)
        : null;

      let allocation = null;
      if (fallbackId) {
        allocation = variant.sellingPlanAllocations.find(
          (a) => String(a.selling_plan_id) === fallbackId
        );
      }
      if (!allocation) {
        allocation = variant.sellingPlanAllocations[0];
      }

      const base = Number(variant.price);
      const sub = Number(allocation.price);
      if (!base || sub == null || sub >= base) return 0;

      return Math.round(((base - sub) / base) * 100);
    },

    /**
     * Base subscription discount % derived from the Skio selling plan allocation.
     * Reads the FIRST selected line's allocation (price vs variant.price). Falls
     * back to the largest available allocation discount in the box if no item has
     * been added yet. Returns 0 if nothing usable is found.
     */
    getBaseSubscriptionDiscount() {
      const selected = this.getSelectedProducts();

      // Prefer the actually-selected items
      for (const entry of selected) {
        const pct = this._allocationDiscountPercent(entry.variant);
        if (pct > 0) return pct;
      }

      // Fallback: scan available products so the badge isn't 0 before anything is added
      for (const product of (this.products || [])) {
        for (const variant of (product.variants || [])) {
          const pct = this._allocationDiscountPercent(variant);
          if (pct > 0) return pct;
        }
      }

      return 0;
    },

    /**
     * Active tier discount % based on current item count. Returns 0 if no tier
     * threshold has been reached.
     */
    getActiveTierDiscount() {
      return Number(this.getActiveTier()?.discount) || 0;
    },

    /**
     * Final stacked badge % — multiplicative: 1 - (1 - sub) * (1 - tier).
     * Rounded to nearest whole %. Returns 0 if both inputs are 0.
     */
    getSubscribeBadgePercent() {
      const sub = this.getBaseSubscriptionDiscount() / 100;
      const tier = this.getActiveTierDiscount() / 100;

      if (sub <= 0 && tier <= 0) return 0;

      const stacked = 1 - (1 - sub) * (1 - tier);
      return Math.round(stacked * 100);
    },

    // -------------------------------------------------------------------------

    getProgressPercent() {
      const max = this.tierConfig[this.tierConfig.length - 1]?.threshold || 1;
      return Math.min(100, Math.round((this.getSelectedItemCount() / max) * 100));
    },

    getTierProgressPercent(tier) {
      const max = this.tierConfig[this.tierConfig.length - 1]?.threshold || 1;
      return Math.min(100, Math.max(0, (Number(tier?.threshold || 0) / max) * 100));
    },

    isTierActive(tier) {
      return this.getSelectedItemCount() >= Number(tier?.threshold || 0);
    },

    getRemainingText() {
      const next = this.getNextTier();
      if (!next) return 'Best discount unlocked';
      const remaining = Math.max(0, next.threshold - this.getSelectedItemCount());
      return `Add ${remaining} to Unlock ${next.discount}%`;
    },

    getSellingPlanId() {
      if (this.typePurchase !== 'subscribe') return '';

      const fromThemeData = this.findThemeSellingPlanId();
      if (fromThemeData) return fromThemeData;
      if (this.fallbackSellingPlanId) return this.fallbackSellingPlanId;

      const fromSkio = this.findSkioSellingPlanId();
      if (fromSkio) return fromSkio;

      const selected = this.getSelectedProducts()[0];
      const allocation = selected?.variant?.sellingPlanAllocations?.[0];
      return allocation?.selling_plan_id ? String(allocation.selling_plan_id) : '';
    },

    findThemeSellingPlanId() {
      const selected = this.getSelectedProducts()[0];
      if (!selected?.product || !selected?.variant) return '';

      const allocationIds = (selected.variant.sellingPlanAllocations || [])
        .map((allocation) => String(allocation.selling_plan_id || ''))
        .filter(Boolean);

      const skioPlans = (selected.product.sellingPlanGroups || [])
        .filter((group) => group.app_id === 'SKIO')
        .flatMap((group) => group.selling_plans || []);

      const normalizedFilter = String(this.sellingPlanNameFilter || '').toLowerCase();
      const normalizedFrequency = String(this.selectedFrequency || '').toLowerCase();
      const matched =
        skioPlans.find((plan) => {
          const name = String(plan.name || '').toLowerCase();
          return normalizedFilter && name.includes(normalizedFilter) && allocationIds.includes(String(plan.id));
        }) ||
        skioPlans.find((plan) => {
          const name = String(plan.name || '').toLowerCase();
          return normalizedFrequency && name.includes(normalizedFrequency) && allocationIds.includes(String(plan.id));
        }) ||
        skioPlans.find((plan) => allocationIds.includes(String(plan.id)));

      return matched?.id ? String(matched.id) : '';
    },

    findSkioSellingPlanId() {
      const groups = [
        this.skioDynamicBox?.sellingPlanGroup,
        this.skioDynamicBox?.DynamicBox?.sellingPlanGroup,
        this.skioDynamicBox?.dynamicBox?.sellingPlanGroup,
        this.skioDynamicBox?.box?.sellingPlanGroup,
      ].filter(Boolean);

      for (const group of groups) {
        const plans = Array.isArray(group)
          ? group
          : group.sellingPlans || group.selling_plans || Object.entries(group).map(([id, plan]) => ({ id, ...plan }));
        const matched =
          plans.find((plan) =>
            this.selectedFrequency &&
            String(plan.name || plan.title || '').toLowerCase().includes(String(this.selectedFrequency).toLowerCase())
          ) ||
          plans[0];
        if (matched?.id) return String(matched.id).replace('gid://shopify/SellingPlan/', '');
        if (matched?.platformId) return String(matched.platformId).replace('gid://shopify/SellingPlan/', '');
      }

      return '';
    },

    getDynamicBox() {
      return (
        this.skioDynamicBox?.DynamicBox ||
        this.skioDynamicBox?.dynamicBox ||
        this.skioDynamicBox?.box ||
        this.skioDynamicBox ||
        {
          boxId: this.skioBoxId,
        }
      );
    },

    buildBundleProperties() {
      return {
        _ctr_bundle_id: `${Date.now()}-${Math.round(Math.random() * 10000)}`,
        _ctr_bundle_count: String(this.getSelectedItemCount()),
      };
    },

    resetSelection() {
      this.bundleEntries = [];
      this.drawerOpen = false;
      this.clearState();
    },

    formatMoney(cents) {
      const value = Number(cents || 0) / 100;
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: window.Shopify?.currency?.active || 'USD',
      }).format(value);
    },

    getProductImage(product) {
      const variant = this.getSelectedVariant(product);
      return variant?.image || product.image || '';
    },

    getProductCardImage(product) {
      // Card-specific: prefer custom.image_for_collection metafield, fall back
      // to standard variant/product image resolution.
      return product?.cardImage || this.getProductImage(product);
    },

    openPopup(product) {
      this.popupProduct = {
        product,
        variant: this.getSelectedVariant(product),
      };
    },

    closePopup() {
      this.popupProduct = null;
    },

    getPopupProduct() {
      return this.popupProduct?.product || null;
    },

    getPopupVariant() {
      return this.popupProduct?.variant || null;
    },

    getPopupImage() {
      const product = this.getPopupProduct();
      const variant = this.getPopupVariant();
      return variant?.image || product?.popup?.image || product?.image || '';
    },

    getPopupTitle() {
      const product = this.getPopupProduct();
      const variant = this.getPopupVariant();
      if (!product) return '';
      if (!variant || variant.title === 'Default Title') return product.title || '';
      return `${product.title} - ${variant.title}`;
    },

    getPopupServingsAmount() {
      const title = String(this.getPopupVariant()?.title || this.getPopupTitle() || '');
      const match = title.match(/(\d+)\s*servings?/i);
      return match ? match[1] : '';
    },

    getPopupDescriptionHtml() {
      return this.getPopupProduct()?.productDescriptionHtml || '';
    },

    addPopupProductToBundle() {
      const product = this.getPopupProduct();
      if (!product) return;
      const variant = this.getPopupVariant();
      if (variant) {
        this.selectedVariantMap[String(product.id)] = variant.id;
      }
      this.addProduct(product);
      this.closePopup();
    },

    async addStackToCart() {
      console.log('[ctr-build-a-box] addStackToCart called');
      const selected = this.getSelectedProducts();
      if (!selected.length || this.isAddingToCart) return;

      this.isAddingToCart = true;
      this.errorMessage = '';

      const properties = this.buildBundleProperties();
      const sellingPlanId = this.getSellingPlanId();
      const dynamicBox = this.getDynamicBox();

      const items = selected.map((entry) => {
        const item = {
          id: entry.variant.id,
          quantity: entry.quantity,
          properties: {
            ...properties,
            _dynamicBoxId: dynamicBox?.boxId || '',
            _dynamicBoxIndex: 0,
          },
        };

        if (this.typePurchase === 'subscribe' && sellingPlanId) {
          item.selling_plan = sellingPlanId;
        }

        return item;
      });

      // Append free product items if configured
      if (this.freeProduct?.enabled && this.freeProduct.products?.length) {
        this.freeProduct.products.forEach((fp) => {
          if (fp.variantId) {
            items.push({
              id: fp.variantId,
              quantity: 1,
              properties: {
                [this.freeProduct.key]: this.freeProduct.value,
              },
            });
          }
        });
      }

      this.debug('cart:add-payload', {
        itemCount: this.getSelectedItemCount(),
        typePurchase: this.typePurchase,
        activeTier: this.getActiveTier(),
        sellingPlanId,
        items,
      });

      try {
        const cartDrawer = document.querySelector('cart-drawer');
        const sections = cartDrawer?.getSectionsToRender?.().map((section) => section.id) || ['cart-drawer', 'cart-icon-bubble'];
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            items,
            sections,
            sections_url: window.location.pathname,
          }),
        });

        const data = await response.json();
        console.log('Добавлено в корзину:', data);
        if (!response.ok) throw new Error(data?.description || data?.message || 'Unable to add bundle');

        if (cartDrawer?.renderContents && data.sections) {
          cartDrawer.renderContents(data);
        } else if (window.Alpine?.store('cart')?.refreshCart) {
          Alpine.store('cart').refreshCart();
        } else if (cartDrawer?.open) {
          cartDrawer.open();
        }

        this.resetSelection();
      } catch (error) {
        this.errorMessage = error?.message || 'Unable to add bundle';
        console.error('ctr-build-a-box: add stack failed', error);
      } finally {
        this.isAddingToCart = false;
      }
    },
  }));
});